import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import nexapi from '../../services/api';
import { notify } from '../../components/UI/notify';
import { NotifyType } from '../../types/enums/notifytype';
import { CompanyModel } from '../../types/models/CompanyModel';
import {RequestStatus} from '../slices/commonEnum';

interface CompanyState {
  data: CompanyModel[];
  totalRecords: number;
  currentPage: number;
  loading: boolean;
  error: string | null;
  status: string | null;
}

interface CompanyResponse {
  Company: CompanyModel[];
  totalCount: number;
}

const initialState: CompanyState = {
  data: [],
  totalRecords: 0,
  currentPage: 0,
  loading: false,
  error: null,
  status: 'idle',
};

const API_URL = '/Company';
const { message } = notify();

// Async thunk for fetching requests
export const fetchCompany = createAsyncThunk<CompanyResponse, {
  filters: Record<string, any>; 
  page: number; 
  numberOfRecord: number;
  userId: string;
  sortColumn: string;
  sortOrder: 'asc' | 'desc';
}>(
  'Company/fetchCompany',
  async ({ filters, page, numberOfRecord, userId, sortColumn = '', sortOrder = 'asc' }) => {
    try {
        const isPaginationEnabled = typeof numberOfRecord === 'number';
      const response = await nexapi.post(API_URL + `/GetAll?pagination=${isPaginationEnabled}&page=${page}&numberOfRecord=${numberOfRecord}&search=${filters?.search ?? null}&sortColumn=${sortColumn}&sortOrder=${sortOrder}&state=${filters.state}`);
      const data = response.data;

      if (Array.isArray(data?.data)) {
        return {
          Company: data.data as CompanyModel[],
          totalCount: data.totalCount,
        };
      } else {
        console.error("Invalid API Response:", data);
        return { Company: [], totalCount: 0 };
      }
    } catch (error) {
      console.error("API Call Failed:", error);
      return { Company: [], totalCount: 0 };
    }
  }
);


export const fetchCompanyDrop = createAsyncThunk<CompanyResponse, {
  filters: Record<string, any>; 
  page: number; 
  numberOfRecord: number;
  userId: string;
  sortColumn: string;
  sortOrder: 'asc' | 'desc';
}>(
  'Company/fetchCompany',
  async ({ filters, page, numberOfRecord, userId, sortColumn = '', sortOrder = 'asc' }) => {
    try {
      const response = await nexapi.post(API_URL + `/GetAll?pagination=false&search=${filters?.search ?? null}&sortColumn=${sortColumn}&sortOrder=${sortOrder}`);
      const data = response.data;

      if (Array.isArray(data?.data)) {
        return {
          Company: data.data as CompanyModel[],
          totalCount: data.totalCount,
        };
      } else {
        console.error("Invalid API Response:", data);
        return { Company: [], totalCount: 0 };
      }
    } catch (error) {
      console.error("API Call Failed:", error);
      return { Company: [], totalCount: 0 };
    }
  }
);

//#region Function to check if the company code is unique
export const isCompanyCodeUnique = async (companyCode: string, id: string): Promise<boolean> => {
  try {
    const response = await nexapi.get(`${API_URL}/IsCompanyCodeUnique?companyCode=${companyCode}&Id=${id}`);
    if(response.data.statusCode == 200){
      return true;
    }
    return false; // returns true if unique, false if not
  } catch (error) {
    console.error("Error checking company code uniqueness", error);
    return false; // In case of error, assume code is not unique
  }
};
//#endregion

export const getCompanyById = createAsyncThunk(
  'Company/getCompanyById',
  async ({ id }: { id: string }) => {
    const response = await nexapi.get(
      API_URL + `/${id}`
    );
    //console.log(response.data.data);
    return {
      Company: response.data,
    };
  }
);

export const createCompany = createAsyncThunk('Company/createCompany', async (Company:CompanyModel) => {
  const response = await nexapi.post(API_URL, Company);
  message(NotifyType.Success, "Company created successfully")
  return response.data;
});

export const updateCompany = createAsyncThunk('Company/updateCompany', async (Company:CompanyModel ) => {
  const response = await nexapi.put(`${API_URL}/${Company.id}`, Company);
  message(NotifyType.Success, "Company updated successfully")
  return response.data;
});

// export const deleteCompany = createAsyncThunk('Company/deleteCompany', async (id:any) => {
//   try {
//   await nexapi.delete(`/Company/${id}`);
//   return id;
// } catch (error) {
//   return error.message
// }
// });


export const deleteCompany = createAsyncThunk<CompanyResponse, { Id : string, UserId:string,reason: string}>(
  'Company/deleteCompany',
  async ({ Id,UserId,reason}) => {
    
    try {
      const [cleanId] = Id.includes('_') ? Id.split('_') : [Id];

      const payload = {
        Id: cleanId,
        UserId: UserId,
        reason: reason
      };
      const url = API_URL+"/ActivateCompany";
      const method = "post";
      const response = await nexapi[method](url, payload);

      
      response.data.statusCode = "200";
      return response.data.statusCode;
    } catch (error) {
      console.error('API Call Failed:', error);
      throw error;
    }
  }
);



const requestsSlice = createSlice({
  name: 'Company',
  initialState,
    reducers: {},
    extraReducers: (builder) => {
      builder
        .addCase(fetchCompany.fulfilled, (state, action) => {
          // Assuming action.payload contains applications and totalCount
          state.data = action.payload.Company;
          state.totalRecords = action.payload.totalCount;
          state.status = RequestStatus.SUCCEEDED;
        })
        .addCase(createCompany.fulfilled, (state, action) => {
          state.data.push(action.payload);
          state.status = RequestStatus.SUCCEEDED;
        })
        .addCase(updateCompany.fulfilled, (state, action) => {
          const index = state.data.findIndex((app) => app.id === action.payload.id);
          if (index !== -1) state.data[index] = action.payload;
          state.status = RequestStatus.SUCCEEDED;
        })
        .addCase(deleteCompany.fulfilled, (state, action:any) => {
          state.data = state.data.filter((app) => app.id !=  action.payload);
          state.status = RequestStatus.SUCCEEDED;
        })
        .addCase(getCompanyById.fulfilled, (state, action) => {
          // Handling single requests data
          const Company = action.payload.Company;
          state.data = [Company]; // Assuming you're replacing the entire data with a single reminider
          state.status = RequestStatus.SUCCEEDED;
        })
        .addMatcher(
          (action) => action.type.endsWith('/pending'),
          (state) => {
            state.status = RequestStatus.LOADING;
          }
        )
        .addMatcher(
          (action) => action.type.endsWith('/rejected'),
          (state, action) => {
            state.status = RequestStatus.FAILED;       
          }
        );
    },
});

export default requestsSlice.reducer;
